import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { Search, X, Clock, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Fuse from "fuse.js";
import { apiClient } from "../../shared/utils/apiClient";
import { normalizeImageUrl } from "../../shared/utils/urlUtils";
import logger from "../../shared/utils/logger";

// Local storage key for recent searches
const RECENT_SEARCHES_KEY = "universal_search_recent";
const MAX_RECENT_SEARCHES = 3;

// Static pages for search (only actual pages, not game categories)
const STATIC_PAGES = [
  { id: "home", name: "Home", path: "/", type: "page" },
  { id: "slots", name: "Slots", path: "/games/slots", type: "page" },
  {
    id: "table-games",
    name: "Table Games",
    path: "/games/table",
    type: "page",
  },
  { id: "live-casino", name: "Live Casino", path: "/games/live", type: "page" },
  {
    id: "instant",
    name: "Instant Games",
    path: "/games/instant",
    type: "page",
  },
  { id: "new-games", name: "New Games", path: "/games/new", type: "page" },
  {
    id: "favorites",
    name: "My Favorites",
    path: "/games/favorites",
    type: "page",
  },
  {
    id: "recently-played",
    name: "Recently Played",
    path: "/games/recent",
    type: "page",
  },
  { id: "providers", name: "All Providers", path: "/providers", type: "page" },
  { id: "promotions", name: "Promotions", path: "/promotions", type: "page" },
  { id: "offers", name: "Coin Offers", path: "/offers", type: "page" },
  { id: "redeem", name: "Redeem Coins", path: "/redeem", type: "page" },
  { id: "vip", name: "VIP Program", path: "/vip", type: "page" },
  { id: "refer", name: "Refer a Friend", path: "/refer", type: "page" },
  { id: "profile", name: "My Profile", path: "/profile", type: "page" },
];

// Format category name for display
const formatCategoryName = (category) => {
  if (!category) return "";
  return category.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

// Fuse.js options for games
const GAME_FUSE_OPTIONS = {
  keys: ["name", "provider"],
  threshold: 0.3,
  includeScore: true,
  minMatchCharLength: 2,
};

// Fuse.js options for categories
const CATEGORY_FUSE_OPTIONS = {
  keys: ["name", "id"],
  threshold: 0.3,
  includeScore: true,
  minMatchCharLength: 2,
};

// Fuse.js options for providers
const PROVIDER_FUSE_OPTIONS = {
  keys: ["name"],
  threshold: 0.3,
  includeScore: true,
  minMatchCharLength: 2,
};

// Fuse.js options for pages
const PAGE_FUSE_OPTIONS = {
  keys: ["name"],
  threshold: 0.4,
  includeScore: true,
  minMatchCharLength: 2,
};

// Cache the search index globally so it persists across component mounts
let cachedSearchIndex = null;
let fetchPromise = null;

const UniversalSearch = ({ autoOpen = false, onClose = null }) => {
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchIndex, setSearchIndex] = useState(
    cachedSearchIndex || { games: [], providers: [], categoryMapping: {} },
  );
  const [, setIndexLoaded] = useState(!!cachedSearchIndex);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [activeTab, setActiveTab] = useState("all");
  const [recentSearches, setRecentSearches] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  const navigate = useNavigate();

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch (e) {
      logger.error("Failed to load recent searches:", e);
    }
  }, []);

  // Save recent search
  const saveRecentSearch = useCallback(
    (item) => {
      try {
        const newRecent = [
          item,
          ...recentSearches.filter(
            (r) => !(r.id === item.id && r.resultType === item.resultType),
          ),
        ].slice(0, MAX_RECENT_SEARCHES);
        setRecentSearches(newRecent);
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(newRecent));
      } catch (e) {
        logger.error("Failed to save recent search:", e);
      }
    },
    [recentSearches],
  );

  // Clear recent searches
  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch (e) {
      logger.error("Failed to clear recent searches:", e);
    }
  }, []);

  // Lock body scroll when open on mobile
  useEffect(() => {
    if (open && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open, isMobile]);

  // Fetch search index when modal opens (lazy load, with global cache)
  useEffect(() => {
    if (!open || cachedSearchIndex) return;

    const fetchSearchIndex = async () => {
      // Prevent duplicate fetches
      if (fetchPromise) {
        const data = await fetchPromise;
        setSearchIndex(data);
        setIndexLoaded(true);
        return;
      }

      fetchPromise = apiClient
        .get("/api/casino/games/search-index")
        .then((response) => {
          cachedSearchIndex = response.data;
          return response.data;
        })
        .catch((error) => {
          logger.error("Failed to fetch search index:", error);
          return { games: [], providers: [], categoryMapping: {} };
        });

      const data = await fetchPromise;
      setSearchIndex(data);
      setIndexLoaded(true);
    };

    fetchSearchIndex();
  }, [open]);

  // Extract unique categories from games using backend category mapping
  const categories = useMemo(() => {
    const { categoryMapping } = searchIndex;
    const categorySet = new Map();

    searchIndex.games.forEach((game) => {
      if (game.category && !categorySet.has(game.category)) {
        // Look up the category in the backend mapping
        const mapping = categoryMapping?.[game.category];
        if (mapping) {
          categorySet.set(game.category, {
            id: game.category,
            name: game.category, // Use raw category name from DB
            path: mapping.route,
            page: mapping.page,
            type: "category",
            gameCount: 0,
          });
        }
        // If not in mapping, skip it - don't create broken links
      }
    });

    // Count games per category
    searchIndex.games.forEach((game) => {
      if (game.category && categorySet.has(game.category)) {
        categorySet.get(game.category).gameCount++;
      }
    });

    return Array.from(categorySet.values());
  }, [searchIndex.games, searchIndex.categoryMapping]);

  // Create Fuse instances
  const gameFuse = useMemo(
    () => new Fuse(searchIndex.games, GAME_FUSE_OPTIONS),
    [searchIndex.games],
  );
  const providerFuse = useMemo(
    () => new Fuse(searchIndex.providers, PROVIDER_FUSE_OPTIONS),
    [searchIndex.providers],
  );
  const pageFuse = useMemo(() => new Fuse(STATIC_PAGES, PAGE_FUSE_OPTIONS), []);
  const categoryFuse = useMemo(
    () => new Fuse(categories, CATEGORY_FUSE_OPTIONS),
    [categories],
  );

  // Perform search
  const searchResults = useMemo(() => {
    if (!query.trim() || query.length < 2) {
      return { games: [], providers: [], pages: [], categories: [] };
    }

    const gameResults = gameFuse
      .search(query)
      .slice(0, 20)
      .map((r) => ({ ...r.item, score: r.score }));
    const providerResults = providerFuse
      .search(query)
      .slice(0, 10)
      .map((r) => ({ ...r.item, score: r.score }));
    const pageResults = pageFuse
      .search(query)
      .slice(0, 5)
      .map((r) => ({ ...r.item, score: r.score }));
    const categoryResults = categoryFuse
      .search(query)
      .slice(0, 5)
      .map((r) => ({ ...r.item, score: r.score }));

    return {
      games: gameResults,
      providers: providerResults,
      pages: pageResults,
      categories: categoryResults,
    };
  }, [query, gameFuse, providerFuse, pageFuse, categoryFuse]);

  // Combined results for keyboard navigation
  const allResults = useMemo(() => {
    const results = [];
    if (activeTab === "all" || activeTab === "categories") {
      searchResults.categories.forEach((c) =>
        results.push({ ...c, resultType: "category" }),
      );
    }
    if (activeTab === "all" || activeTab === "pages") {
      searchResults.pages.forEach((p) =>
        results.push({ ...p, resultType: "page" }),
      );
    }
    if (activeTab === "all" || activeTab === "providers") {
      searchResults.providers.forEach((p) =>
        results.push({ ...p, resultType: "provider" }),
      );
    }
    if (activeTab === "all" || activeTab === "games") {
      searchResults.games.forEach((g) =>
        results.push({ ...g, resultType: "game" }),
      );
    }
    return results;
  }, [searchResults, activeTab]);

  // Auto-open support
  useEffect(() => {
    if (autoOpen) {
      setOpen(true);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [autoOpen]);

  // Handle close
  const handleClose = useCallback(() => {
    setOpen(false);
    setQuery("");
    setSelectedIndex(-1);
    onClose?.();
  }, [onClose]);

  // Convert name to URL slug (matches ProviderPage.nameToSlug)
  const nameToSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with single
      .trim();
  };

  // Handle result selection
  const handleSelect = useCallback(
    (item) => {
      // Save to recent searches
      saveRecentSearch(item);

      if (item.resultType === "game") {
        navigate(`/games/${item.id}`);
      } else if (item.resultType === "provider") {
        // Create slug from provider name (matches ProviderPage matching logic)
        const providerSlug = nameToSlug(item.name || item.code);
        navigate(`/games/provider/${providerSlug}`);
      } else if (item.resultType === "category") {
        // Navigate to the parent category page with subcategory filter pre-applied
        // e.g., "arcade" → /games/instant with subcategory="arcade" in state
        navigate(item.path, { state: { subcategory: item.id } });
      } else if (item.resultType === "page") {
        navigate(item.path);
      }
      handleClose();
    },
    [navigate, handleClose, saveRecentSearch],
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        handleClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, allResults.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, -1));
      } else if (
        e.key === "Enter" &&
        selectedIndex >= 0 &&
        allResults[selectedIndex]
      ) {
        e.preventDefault();
        handleSelect(allResults[selectedIndex]);
      }
    },
    [handleClose, allResults, selectedIndex, handleSelect],
  );

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, handleClose]);

  // Global keyboard shortcut (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    };
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  // Gradient icon component - dark background with gradient stroke icon (matching site icons)
  const GradientIcon = ({ type }) => {
    const gradientId = `searchGradient-${type}-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className="w-10 h-10 rounded-lg bg-card flex items-center justify-center">
        <svg
          className="w-5 h-5"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FFE48A" />
              <stop offset="50%" stopColor="var(--color-primary)" />
              <stop offset="100%" stopColor="#A87A1F" />
            </linearGradient>
          </defs>
          {type === "game" && (
            <path
              stroke={`url(#${gradientId})`}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 0 1-.657.643 48.39 48.39 0 0 1-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 0 1-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 0 0-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 0 1-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 0 0 .657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 0 1-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 0 0 5.427-.63 48.05 48.05 0 0 0 .582-4.717.532.532 0 0 0-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.959.401v0a.656.656 0 0 0 .659-.663 47.703 47.703 0 0 0-.31-4.82 47.646 47.646 0 0 1-4.163.3.64.64 0 0 1-.657-.643v0Z"
            />
          )}
          {type === "provider" && (
            <path
              stroke={`url(#${gradientId})`}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z"
            />
          )}
          {type === "category" && (
            <path
              stroke={`url(#${gradientId})`}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z"
            />
          )}
          {type === "page" && (
            <path
              stroke={`url(#${gradientId})`}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
            />
          )}
        </svg>
      </div>
    );
  };

  const getResultIcon = (type) => <GradientIcon type={type} />;

  const totalResults =
    searchResults.games.length +
    searchResults.providers.length +
    searchResults.pages.length +
    searchResults.categories.length;

  // Render a single result item (reused for both results and recent)
  const renderResultItem = (item, index, isRecent = false) => (
    <button
      key={`${isRecent ? "recent-" : ""}${item.resultType}-${item.id || item.code}`}
      onClick={() => handleSelect(item)}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer ${
        !isRecent && index === selectedIndex
          ? "bg-primary/20 border-l-2 border-primary"
          : "hover:bg-muted border-l-2 border-transparent"
      }`}
    >
      {item.resultType === "game" && item.thumb ? (
        <img
          src={normalizeImageUrl(item.thumb)}
          alt={item.name}
          className="w-10 h-10 rounded-lg object-cover"
        />
      ) : item.resultType === "provider" && item.logo ? (
        <img
          src={normalizeImageUrl(item.logo)}
          alt={item.name}
          className="w-10 h-10 rounded-lg object-contain bg-white p-1"
        />
      ) : (
        getResultIcon(item.resultType)
      )}
      <div className="flex-1 min-w-0">
        <div className="text-white font-medium truncate">{item.name}</div>
        <div className="text-sm text-gray-400 truncate">
          {item.resultType === "game" && (
            <>
              {item.provider}
              {item.category && (
                <span className="ml-2 text-gray-500">
                  • {formatCategoryName(item.category)}
                </span>
              )}
            </>
          )}
          {item.resultType === "category" && `${item.gameCount} games`}
          {item.resultType === "provider" && `${item.gameCount} games`}
          {item.resultType === "page" && item.path}
        </div>
      </div>
      <span className="text-xs text-gray-500 capitalize px-2 py-1 bg-card rounded">
        {item.resultType}
      </span>
    </button>
  );

  // Desktop modal content
  const desktopContent = (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[10vh] bg-black/60 backdrop-blur-sm">
      <div
        ref={dropdownRef}
        className="w-full max-w-2xl mx-4 bg-background rounded-xl shadow-2xl border border-border overflow-hidden"
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 p-4 border-b border-border">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(-1);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search games, providers, pages..."
            className="flex-1 bg-transparent text-white placeholder-gray-500 outline-none text-lg"
            autoFocus
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="p-1.5 hover:bg-muted rounded-md transition-colors cursor-pointer"
            >
              <X className="w-5 h-5 text-gray-400 hover:text-white" />
            </button>
          )}
        </div>

        {/* Tabs */}
        {query.length >= 2 && totalResults > 0 && (
          <div className="flex gap-1 px-4 py-2 border-b border-border bg-card/50 overflow-x-auto">
            {[
              { key: "all", label: `All (${totalResults})` },
              { key: "games", label: `Games (${searchResults.games.length})` },
              {
                key: "categories",
                label: `Categories (${searchResults.categories.length})`,
              },
              {
                key: "providers",
                label: `Providers (${searchResults.providers.length})`,
              },
              { key: "pages", label: `Pages (${searchResults.pages.length})` },
            ]
              .filter(
                (tab) =>
                  tab.key === "all" ||
                  (tab.key === "games" && searchResults.games.length > 0) ||
                  (tab.key === "categories" &&
                    searchResults.categories.length > 0) ||
                  (tab.key === "providers" &&
                    searchResults.providers.length > 0) ||
                  (tab.key === "pages" && searchResults.pages.length > 0),
              )
              .map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setSelectedIndex(-1);
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === tab.key
                      ? "bg-primary/10 text-primary border border-primary/20"
                      : "text-foreground hover:bg-muted hover:text-white border border-transparent"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
          </div>
        )}

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {query.length < 2 ? (
            <>
              {/* Recent Searches */}
              {recentSearches.length > 0 && (
                <div className="border-b border-border">
                  <div className="flex items-center justify-between px-4 py-2 bg-card/50">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Clock className="w-4 h-4" />
                      <span>Recent</span>
                    </div>
                    <button
                      onClick={clearRecentSearches}
                      className="text-xs text-gray-500 hover:text-white transition-colors cursor-pointer"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="py-1">
                    {recentSearches.map((item, index) =>
                      renderResultItem(item, index, true),
                    )}
                  </div>
                </div>
              )}
              <div className="p-8 text-center text-gray-500">
                {/* Sherlock chicken empty-state mascot. Square wrapper + object-cover
                    object-top crops the bottom off the (taller-than-wide) PNG so
                    we get a clean head-and-shoulders portrait. max-w cap keeps
                    it from looking heavy at any breakpoint. */}
                <div className="mx-auto mb-3 w-40 h-40 max-w-[200px] overflow-hidden">
                  <img
                    src="/sherlock-chicken.png"
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                    className="w-full h-full object-cover object-top select-none scale-110 origin-top"
                  />
                </div>
                <p>Type at least 2 characters to search</p>
                <p className="text-sm mt-2 text-gray-600">
                  Press{" "}
                  <kbd className="px-2 py-0.5 bg-card rounded text-xs">
                    ⌘K
                  </kbd>{" "}
                  anytime to search
                </p>
              </div>
            </>
          ) : allResults.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              No results found for "{query}"
            </div>
          ) : (
            <div className="py-2">
              {allResults.map((item, index) => renderResultItem(item, index))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Mobile full-screen content
  const mobileContent = (
    <div className="fixed inset-0 z-[9999] bg-background flex flex-col safe-area-inset-top safe-area-inset-bottom">
      {/* Header with back arrow */}
      <div className="flex items-center p-4 border-b border-border bg-background">
        <button
          onClick={handleClose}
          className="p-2 text-gray-400 hover:text-white transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="flex-1 flex items-center gap-3 ml-2">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(-1);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search games, providers, pages..."
            className="flex-1 bg-transparent text-white placeholder-gray-500 outline-none text-lg"
            autoFocus
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="p-1.5 hover:bg-muted rounded-md transition-colors cursor-pointer"
            >
              <X className="w-5 h-5 text-gray-400 hover:text-white" />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      {query.length >= 2 && totalResults > 0 && (
        <div className="flex gap-1 px-4 py-2 border-b border-border bg-card/50 overflow-x-auto">
          {[
            { key: "all", label: `All (${totalResults})` },
            { key: "games", label: `Games (${searchResults.games.length})` },
            {
              key: "categories",
              label: `Categories (${searchResults.categories.length})`,
            },
            {
              key: "providers",
              label: `Providers (${searchResults.providers.length})`,
            },
            { key: "pages", label: `Pages (${searchResults.pages.length})` },
          ]
            .filter(
              (tab) =>
                tab.key === "all" ||
                (tab.key === "games" && searchResults.games.length > 0) ||
                (tab.key === "categories" &&
                  searchResults.categories.length > 0) ||
                (tab.key === "providers" &&
                  searchResults.providers.length > 0) ||
                (tab.key === "pages" && searchResults.pages.length > 0),
            )
            .map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  setSelectedIndex(-1);
                }}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === tab.key
                    ? "bg-primary/10 text-primary border border-primary/20"
                    : "text-foreground hover:bg-muted hover:text-white border border-transparent"
                }`}
              >
                {tab.label}
              </button>
            ))}
        </div>
      )}

      {/* Results */}
      <div className="flex-1 overflow-y-auto">
        {query.length < 2 ? (
          <>
            {/* Recent Searches */}
            {recentSearches.length > 0 && (
              <div className="border-b border-border">
                <div className="flex items-center justify-between px-4 py-2 bg-card/50">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span>Recent</span>
                  </div>
                  <button
                    onClick={clearRecentSearches}
                    className="text-xs text-gray-500 hover:text-white transition-colors cursor-pointer"
                  >
                    Clear
                  </button>
                </div>
                <div className="py-1">
                  {recentSearches.map((item, index) =>
                    renderResultItem(item, index, true),
                  )}
                </div>
              </div>
            )}
            <div className="p-8 text-center text-gray-500">
              {/* Sherlock chicken empty-state mascot. Square wrapper + object-cover
                    object-top crops the bottom off the (taller-than-wide) PNG so
                    we get a clean head-and-shoulders portrait. max-w cap keeps
                    it from looking heavy at any breakpoint. */}
              <div className="mx-auto mb-3 w-40 h-40 max-w-[200px] overflow-hidden">
                <img
                  src="/sherlock-chicken.png"
                  alt=""
                  aria-hidden="true"
                  draggable={false}
                  className="w-full h-full object-cover object-top select-none scale-110 origin-top"
                />
              </div>
              <p>Type at least 2 characters to search</p>
            </div>
          </>
        ) : allResults.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            No results found for "{query}"
          </div>
        ) : (
          <div className="py-2">
            {allResults.map((item, index) => renderResultItem(item, index))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Search trigger button */}
      <button
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        className="flex items-center gap-2 px-4 py-2 bg-card hover:bg-muted rounded-lg text-gray-400 hover:text-white transition-colors cursor-pointer"
      >
        <Search className="w-4 h-4" />
        <span className="hidden sm:inline text-sm">Search anything...</span>
        <kbd className="hidden md:inline px-1.5 py-0.5 text-xs bg-card rounded">
          ⌘K
        </kbd>
      </button>

      {/* Search modal portal */}
      {open &&
        createPortal(isMobile ? mobileContent : desktopContent, document.body)}
    </>
  );
};

export default UniversalSearch;
